// Page Artisans - Design Premium 10/10
// Système de filtres unifié Amazon/Airbnb style

import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, MapPin, Star, Phone, ChevronRight, ArrowLeft,
  Building2, CheckCircle, X, Wrench, Zap, Droplets, Flame,
  Home, Brush, Hammer, Shield, Users, SlidersHorizontal, Layers,
  AlertTriangle, Info, Euro, Tag, Waves, TreeDeciduous, Wind, Ruler, Filter,
  MessageSquare, List, Map
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import UnifiedHeader from '../../../components/layout/UnifiedHeader';
import MainNavigation from '../../../components/layout/MainNavigation';
import UniversalBanner from '../../../components/layout/UniversalBanner';
import UnifiedFilterSystem, { ActiveFilterBadges, FilterButton, FilterContextPhrase } from '../../../components/filters/UnifiedFilterSystem';
import { useDemoMode } from '../../../components/demo/DemoModeProvider';
import { CardSkeleton } from '../../../components/ui/skeletons';
import { AdSidebarLeft, AdSidebarRight, AdBannerMobile } from '../../../components/ads/AdBannerSystem';
import ArtisansMapView from '../../../components/maps/ArtisansMapView';

import { API_URL } from '../../../config/api';

// Métiers avec icônes et couleurs - Liste complète (incluant Jardinier)
const METIERS = [
  { id: 'Plombier', icon: Droplets, color: '#0EA5E9', label: 'Plombier' },
  { id: 'Électricien', icon: Zap, color: '#F59E0B', label: 'Électricien' },
  { id: 'Maçon', icon: Building2, color: '#6B7280', label: 'Maçon' },
  { id: 'Carreleur', icon: Ruler, color: '#10B981', label: 'Carreleur' },
  { id: 'Peintre', icon: Brush, color: '#EC4899', label: 'Peintre' },
  { id: 'Menuisier', icon: Hammer, color: '#92400E', label: 'Menuisier' },
  { id: 'Couvreur', icon: Home, color: '#7C3AED', label: 'Couvreur' },
  { id: 'Chauffagiste', icon: Flame, color: '#EF4444', label: 'Chauffagiste' },
  { id: 'Pisciniste', icon: Waves, color: '#3B82F6', label: 'Pisciniste' },
  { id: 'Paysagiste', icon: TreeDeciduous, color: '#22C55E', label: 'Paysagiste' },
  { id: 'Jardinier', icon: TreeDeciduous, color: '#16A34A', label: 'Jardinier' },
  { id: 'Climaticien', icon: Wind, color: '#06B6D4', label: 'Climaticien' },
  { id: 'Façadier', icon: Building2, color: '#8B5CF6', label: 'Façadier' },
  { id: 'Serrurier', icon: Wrench, color: '#64748B', label: 'Serrurier' },
  { id: 'Vitrier', icon: Home, color: '#14B8A6', label: 'Vitrier' },
  { id: 'Charpentier', icon: Hammer, color: '#A16207', label: 'Charpentier' },
];

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DE TEST - PRÉOUVERTURE ARTISANS
// ══════════════════════════════════════════════════════════════════════════════

const MOCK_ARTISANS = [
  {
    id: 'artisan-1',
    slug: 'plomberie-martin-lyon',
    shop_name: 'Plomberie Martin & Fils',
    metier: 'Plombier',
    description: 'Entreprise familiale depuis 1985. Spécialistes en plomberie générale, chauffage et sanitaires. Intervention rapide 7j/7.',
    city: 'Lyon',
    postal_code: '69003',
    phone: '04 78 XX XX XX',
    is_verified: true,
    rating_average: 4.9,
    reviews_count: 127,
    cover_image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800',
    logo: null,
    services: ['Dépannage urgent', 'Installation sanitaire', 'Rénovation salle de bain', 'Chauffe-eau']
  },
  {
    id: 'artisan-2',
    slug: 'elec-pro-marseille',
    shop_name: 'Élec Pro Méditerranée',
    metier: 'Électricien',
    description: 'Électriciens certifiés Qualifelec. Mise aux normes, domotique, bornes de recharge. Devis gratuit sous 24h.',
    city: 'Marseille',
    postal_code: '13008',
    phone: '04 91 XX XX XX',
    is_verified: true,
    rating_average: 4.8,
    reviews_count: 89,
    cover_image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800',
    logo: null,
    services: ['Mise aux normes', 'Tableau électrique', 'Domotique', 'Borne de recharge']
  },
  {
    id: 'artisan-3',
    slug: 'maconnerie-dupont-toulouse',
    shop_name: 'Maçonnerie Dupont',
    metier: 'Maçon',
    description: 'Maçonnerie traditionnelle et moderne. Construction, rénovation, extension. Travail soigné garanti.',
    city: 'Toulouse',
    postal_code: '31000',
    phone: '05 61 XX XX XX',
    is_verified: true,
    rating_average: 4.7,
    reviews_count: 64,
    cover_image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    logo: null,
    services: ['Construction neuve', 'Extension', 'Rénovation', 'Murs de clôture']
  },
  {
    id: 'artisan-4',
    slug: 'carrelage-concept-nice',
    shop_name: 'Carrelage Concept Côte d\'Azur',
    metier: 'Carreleur',
    description: 'Pose de carrelage haut de gamme. Spécialistes grands formats, mosaïque et pierre naturelle.',
    city: 'Nice',
    postal_code: '06000',
    phone: '04 93 XX XX XX',
    is_verified: true,
    rating_average: 4.9,
    reviews_count: 112,
    cover_image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800',
    logo: null,
    services: ['Carrelage intérieur', 'Carrelage extérieur', 'Mosaïque', 'Pierre naturelle']
  },
  {
    id: 'artisan-5',
    slug: 'peinture-artdeco-bordeaux',
    shop_name: 'Art\'Déco Peinture',
    metier: 'Peintre',
    description: 'Peinture décorative et technique. Ravalement, papier peint, effets décoratifs. Artisan d\'art.',
    city: 'Bordeaux',
    postal_code: '33000',
    phone: '05 56 XX XX XX',
    is_verified: true,
    rating_average: 4.8,
    reviews_count: 78,
    cover_image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800',
    logo: null,
    services: ['Peinture intérieure', 'Peinture extérieure', 'Papier peint', 'Effets décoratifs']
  },
  {
    id: 'artisan-6',
    slug: 'menuiserie-bois-nantes',
    shop_name: 'L\'Atelier du Bois',
    metier: 'Menuisier',
    description: 'Menuiserie sur mesure. Fabrication artisanale de meubles, cuisines, dressings et agencements.',
    city: 'Nantes',
    postal_code: '44000',
    phone: '02 40 XX XX XX',
    is_verified: true,
    rating_average: 5.0,
    reviews_count: 56,
    cover_image: 'https://images.unsplash.com/photo-1616401784845-180882ba9ba8?w=800',
    logo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200',
    services: ['Cuisine sur mesure', 'Dressing', 'Escalier bois', 'Mobilier']
  },
  {
    id: 'artisan-13',
    slug: 'plomberie-excellence-paris',
    shop_name: 'Plomberie Excellence Paris',
    metier: 'Plombier',
    description: 'Plomberie haut de gamme à Paris. Interventions soignées, matériaux premium. Service 5 étoiles garanti.',
    city: 'Paris',
    postal_code: '75008',
    phone: '01 42 XX XX XX',
    is_verified: true,
    rating_average: 5.0,
    reviews_count: 89,
    cover_image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=800',
    logo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
    services: ['Plomberie luxe', 'Salle de bain complète', 'Dépannage VIP', 'Chauffe-eau premium']
  },
  {
    id: 'artisan-14',
    slug: 'electricite-pro-lyon',
    shop_name: 'Électricité Pro Lyon',
    metier: 'Électricien',
    description: 'Électricien certifié Qualifelec. Domotique, mise aux normes, installations complexes. Qualité irréprochable.',
    city: 'Lyon',
    postal_code: '69002',
    phone: '04 72 XX XX XX',
    is_verified: true,
    rating_average: 5.0,
    reviews_count: 124,
    cover_image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800',
    logo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200',
    services: ['Domotique', 'Tableau électrique', 'Mise aux normes', 'Borne de recharge']
  },
  {
    id: 'artisan-15',
    slug: 'peinture-art-bordeaux',
    shop_name: 'Peinture Art & Design',
    metier: 'Peintre',
    description: 'Peinture décorative haut de gamme. Effets spéciaux, fresques murales, finitions parfaites.',
    city: 'Bordeaux',
    postal_code: '33000',
    phone: '05 56 XX XX XX',
    is_verified: true,
    rating_average: 5.0,
    reviews_count: 67,
    cover_image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800',
    logo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200',
    services: ['Peinture décorative', 'Effets spéciaux', 'Fresque murale', 'Papier peint luxe']
  },
  {
    id: 'artisan-16',
    slug: 'carrelage-prestige-nice',
    shop_name: 'Carrelage Prestige Côte d\'Azur',
    metier: 'Carreleur',
    description: 'Pose de carrelage prestige. Marbre, pierre naturelle, grands formats. Finitions millimétrées.',
    city: 'Nice',
    postal_code: '06000',
    phone: '04 93 XX XX XX',
    is_verified: true,
    rating_average: 5.0,
    reviews_count: 78,
    cover_image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800',
    logo: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200',
    services: ['Marbre', 'Pierre naturelle', 'Grands formats', 'Mosaïque art']
  },
  {
    id: 'artisan-7',
    slug: 'toiture-expertise-lille',
    shop_name: 'Toiture Expertise Nord',
    metier: 'Couvreur',
    description: 'Couverture, zinguerie et isolation de toiture. Urgence fuite 24h/24. Garantie décennale.',
    city: 'Lille',
    postal_code: '59000',
    phone: '03 20 XX XX XX',
    is_verified: true,
    rating_average: 4.6,
    reviews_count: 93,
    cover_image: 'https://images.pexels.com/photos/31771166/pexels-photo-31771166.jpeg?auto=compress&cs=tinysrgb&w=800',
    logo: null,
    services: ['Réfection toiture', 'Zinguerie', 'Isolation combles', 'Urgence fuite']
  },
  {
    id: 'artisan-8',
    slug: 'chauffage-confort-strasbourg',
    shop_name: 'Chauffage Confort Plus',
    metier: 'Chauffagiste',
    description: 'Installation et entretien de systèmes de chauffage. PAC, chaudières, plancher chauffant.',
    city: 'Strasbourg',
    postal_code: '67000',
    phone: '03 88 XX XX XX',
    is_verified: true,
    rating_average: 4.7,
    reviews_count: 67,
    cover_image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800',
    logo: null,
    services: ['Pompe à chaleur', 'Chaudière gaz', 'Plancher chauffant', 'Entretien annuel']
  },
  {
    id: 'artisan-9',
    slug: 'piscine-azur-montpellier',
    shop_name: 'Piscines Azur Méditerranée',
    metier: 'Pisciniste',
    description: 'Construction et rénovation de piscines. Liner, béton, traitement automatique. SAV réactif.',
    city: 'Montpellier',
    postal_code: '34000',
    phone: '04 67 XX XX XX',
    is_verified: true,
    rating_average: 4.8,
    reviews_count: 45,
    cover_image: 'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?w=800',
    logo: null,
    services: ['Construction piscine', 'Rénovation', 'Traitement eau', 'Hivernage']
  },
  {
    id: 'artisan-10',
    slug: 'jardin-creation-rennes',
    shop_name: 'Jardins & Créations',
    metier: 'Paysagiste',
    description: 'Conception et création de jardins. Terrasses, clôtures, arrosage automatique. Entretien.',
    city: 'Rennes',
    postal_code: '35000',
    phone: '02 99 XX XX XX',
    is_verified: true,
    rating_average: 4.9,
    reviews_count: 88,
    cover_image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800',
    logo: null,
    services: ['Création jardin', 'Terrasse bois', 'Clôture', 'Arrosage automatique']
  },
  {
    id: 'artisan-11',
    slug: 'clim-pro-aix',
    shop_name: 'Clim\'Pro Provence',
    metier: 'Climaticien',
    description: 'Installation et maintenance climatisation. Réversible, gainable, multi-split. Certification RGE.',
    city: 'Aix-en-Provence',
    postal_code: '13100',
    phone: '04 42 XX XX XX',
    is_verified: true,
    rating_average: 4.7,
    reviews_count: 52,
    cover_image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800',
    logo: null,
    services: ['Clim réversible', 'Gainable', 'Multi-split', 'Entretien']
  },
  {
    id: 'artisan-12',
    slug: 'facade-renovation-dijon',
    shop_name: 'Façades & Rénovation',
    metier: 'Façadier',
    description: 'Ravalement de façades, ITE, crépi. Traitement des fissures et imperméabilisation.',
    city: 'Dijon',
    postal_code: '21000',
    phone: '03 80 XX XX XX',
    is_verified: true,
    rating_average: 4.6,
    reviews_count: 41,
    cover_image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    logo: null,
    services: ['Ravalement', 'ITE', 'Crépi', 'Traitement fissures']
  }
];

export { MOCK_ARTISANS };

// Shop Card Component - Premium Design avec badge TEST
function ShopCard({ shop, index }) {
  const isTest = shop.id?.startsWith('artisan-');
  const { requireAuth } = useDemoMode();
  
  const handlePhoneClick = (e) => {
    e.preventDefault();
    if (requireAuth('contacter cet artisan')) {
      // User authenticated, allow phone call
      window.location.href = `tel:${shop.phone}`;
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-2xl overflow-hidden group relative h-full flex flex-col"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
      whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0,0,0,0.12)' }}
    >
      {/* Cover Image */}
      <div className="h-36 relative overflow-hidden">
        {shop.cover_image ? (
          <img
            src={shop.cover_image}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#2F4EA2] to-[#1E40AF]" />
        )}
        
        {/* Badge TEST pour préouverture */}
        {isTest && (
          <div className="absolute top-3 left-3 bg-amber-500 text-white px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 shadow-lg">
            <Info size={12} />
            TEST
          </div>
        )}
        
        {/* Badge vérifié */}
        {shop.is_verified && (
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-3 right-3 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg"
          >
            <CheckCircle size={14} />
            Vérifié
          </motion.div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>

      {/* Logo */}
      <div className="relative px-5 -mt-10">
        <div className="w-20 h-20 rounded-2xl bg-white shadow-xl border-4 border-white overflow-hidden">
          {shop.logo ? (
            <img src={shop.logo} alt={shop.shop_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <Building2 size={32} className="text-gray-400" />
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-5 pt-3 flex flex-col flex-grow">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-lg text-gray-900 group-hover:text-[#F97316] transition-colors">{shop.shop_name}</h3>
              {/* Badge Bêta petit - à droite du nom */}
              {shop.is_demo && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-[9px] font-medium text-amber-600">
                  <span className="w-1 h-1 bg-amber-400 rounded-full"></span>
                  Démo
                </span>
              )}
            </div>
            <p className="text-[#1E40AF] font-semibold text-sm mt-0.5">{shop.metier}</p>
          </div>
          
          {/* Rating */}
          {shop.reviews_count > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1.5 rounded-xl">
              <Star size={16} className="text-amber-500 fill-amber-500" />
              <span className="font-bold text-gray-900">{shop.rating_average?.toFixed(1)}</span>
              <span className="text-gray-500 text-sm">({shop.reviews_count})</span>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-gray-500 mt-3">
          <MapPin size={15} />
          <span className="text-sm font-medium">{shop.city} ({shop.postal_code})</span>
        </div>

        {/* Description */}
        {shop.description && (
          <p className="text-gray-600 text-sm mt-3 line-clamp-2 leading-relaxed">{shop.description}</p>
        )}

        {/* Services Tags */}
        {shop.services && shop.services.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {shop.services.slice(0, 3).map((service, idx) => (
              <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                {typeof service === 'string' ? service : service.name}
              </span>
            ))}
            {shop.services.length > 3 && (
              <span className="text-xs text-gray-500 px-2 py-1">+{shop.services.length - 3}</span>
            )}
          </div>
        )}

        {/* Actions - Toujours en bas de la carte */}
        <div className="flex items-center gap-3 mt-auto pt-5">
          <Link to={`/artisans/${shop.seller_id || shop.slug || shop.id}`} className="flex-1">
            <motion.div
              className="w-full py-3 rounded-xl text-center font-semibold text-white text-sm"
              style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
              whileHover={{ scale: 1.02, boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)' }}
              whileTap={{ scale: 0.98 }}
            >
              Voir le {shop.metier || 'Artisan'}
            </motion.div>
          </Link>
          <motion.button 
            onClick={handlePhoneClick}
            className="w-12 h-12 bg-[#1a2a4a] rounded-xl flex items-center justify-center hover:bg-[#2d4a6a] transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="contact-artisan-btn"
            title="Envoyer un message"
          >
            <MessageSquare size={20} className="text-white" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PANNEAU DE FILTRES - Style unifié avec Marketplace et Entre Particuliers
// ══════════════════════════════════════════════════════════════════════════════

function ArtisansFilterPanel({ isOpen, onClose, onApplyFilters, currentFilters }) {
  const [selectedMetier, setSelectedMetier] = useState(currentFilters.metier || '');
  const [selectedCity, setSelectedCity] = useState(currentFilters.city || '');
  const [selectedRayon, setSelectedRayon] = useState(currentFilters.rayon || '');
  const [verifiedOnly, setVerifiedOnly] = useState(currentFilters.verified || false);

  const VILLES = [
    'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice', 'Bordeaux', 
    'Nantes', 'Lille', 'Strasbourg', 'Montpellier', 'Rennes', 
    'Aix-en-Provence', 'Dijon', 'Grenoble', 'Toulon'
  ];

  const applyFilters = () => {
    onApplyFilters({
      metier: selectedMetier,
      city: selectedCity,
      rayon: selectedRayon,
      verified: verifiedOnly
    });
    onClose();
  };

  const resetFilters = () => {
    setSelectedMetier('');
    setSelectedCity('');
    setSelectedRayon('');
    setVerifiedOnly(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-white z-50 flex flex-col"
          >
            {/* Header */}
            <div className="bg-[#1E40AF] px-4 py-4 flex items-center justify-between">
              <h2 className="text-white font-bold text-lg flex items-center gap-2">
                <SlidersHorizontal size={20} />
                Filtres
              </h2>
              <button onClick={onClose} className="text-white/80 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Métiers */}
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Wrench size={18} className="text-[#1E40AF]" />
                  Métiers
                </h3>
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedMetier('')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      !selectedMetier ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    Tous les métiers
                  </button>
                  {METIERS.map(metier => {
                    const Icon = metier.icon;
                    return (
                      <button
                        key={metier.id}
                        onClick={() => setSelectedMetier(metier.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedMetier === metier.id ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={16} style={{ color: selectedMetier === metier.id ? 'white' : metier.color }} />
                        {metier.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Villes */}
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin size={18} className="text-[#F97316]" />
                  Ville
                </h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  <button
                    onClick={() => setSelectedCity('')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      !selectedCity ? 'bg-[#F97316] text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    Toutes les villes
                  </button>
                  {VILLES.map(ville => (
                    <button
                      key={ville}
                      onClick={() => setSelectedCity(ville)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCity === ville ? 'bg-[#F97316] text-white' : 'hover:bg-gray-100'
                      }`}
                    >
                      {ville}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rayon d'intervention - NOUVEAU */}
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Layers size={18} className="text-blue-500" />
                  Zone d'intervention
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Artisans qui interviennent dans ce rayon minimum
                </p>
                <div className="space-y-1">
                  {[
                    { value: '', label: 'Tous les rayons' },
                    { value: '10', label: 'Jusqu\'à 10 km' },
                    { value: '25', label: 'Jusqu\'à 25 km' },
                    { value: '50', label: 'Jusqu\'à 50 km' },
                    { value: '100', label: 'Jusqu\'à 100 km' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedRayon(option.value)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedRayon === option.value ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-3">Options</h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Artisans vérifiés uniquement</span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={resetFilters}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={applyFilters}
                  className="flex-1 py-3 px-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-[#EA580C] transition-colors"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Main Artisans Page - Premium
export default function ArtisansPage({ embedded = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Mode d'affichage: 'liste' ou 'carte'
  const [viewMode, setViewMode] = useState('liste');
  
  // Nouveau système de filtres unifié
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeFilterSection, setActiveFilterSection] = useState(null);

  // Liste des villes disponibles pour la recherche
  const AVAILABLE_CITIES = [...new Set(MOCK_ARTISANS.map(a => a.city))];

  // État pour les artisans de l'API
  const [apiArtisans, setApiArtisans] = useState([]);
  const [boutiqueShops, setBoutiqueShops] = useState([]);
  const [searchApiParams, setSearchApiParams] = useState({});
  const [facets, setFacets] = useState({ metiers: [] });

  // Charger les artisans depuis l'API au montage et quand les filtres changent
  useEffect(() => {
    const loadArtisans = async () => {
      try {
        // Construire les paramètres de recherche
        const params = new URLSearchParams({ per_page: '100' });
        
        // Ajouter les filtres actifs à l'API
        // Support both singular 'metier' (UnifiedFilterSystem) and plural 'metiers' (array from header)
        const metierList = filters.metiers?.length > 0 ? filters.metiers : (filters.metier ? [filters.metier] : []);
        if (metierList.length === 1) {
          params.append('metier', metierList[0]);
        }
        if (filters.ville) {
          params.append('city', filters.ville);
        }
        if (filters.rayon) {
          params.append('rayon_km', filters.rayon);
        }
        if (filters.certifie) {
          params.append('verified_only', 'true');
        }
        if (filters.prestation) {
          params.append('prestation', filters.prestation);
        }
        if (filters.disponibilite) {
          params.append('disponible', 'true');
        }
        if (filters.note) {
          params.append('min_rating', filters.note);
        }
        
        const response = await fetch(`${API_URL}/api/artisans?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          
          // Stocker les facettes pour les filtres dynamiques
          if (data.facets?.metiers) {
            setFacets(data.facets);
          }
          
          // Images de démo par métier
          const DEMO_IMAGES = {
            'electricien': 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800',
            'plombier': 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=800',
            'macon': 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
            'carreleur': 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800',
            'peintre': 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800',
            'menuisier': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
            'couvreur': 'https://images.pexels.com/photos/31771166/pexels-photo-31771166.jpeg?auto=compress&cs=tinysrgb&w=800',
            'toiture': 'https://images.pexels.com/photos/31771166/pexels-photo-31771166.jpeg?auto=compress&cs=tinysrgb&w=800',
            'chauffagiste': 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800',
            'pisciniste': 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800',
            'paysagiste': 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800',
            'default': 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'
          };
          
          // Images de profil HOMMES uniquement - 20 photos artisans/professionnels
          const DEMO_LOGOS = [
            'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1599566150163-29194dcabd36?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1639149888905-fb39731f2e6c?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1628157588553-5eeea00af15c?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1463453091185-61582044d556?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1583195764036-6dc248ac07d9?w=400&h=400&fit=crop',
            'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=400&h=400&fit=crop',
          ];
          
          // Transformer les données API vers le format attendu
          // Calculer les index pour éviter les doublons côte à côte
          const usedIndices = [];
          
          const transformed = (data.artisans || []).map((a, index) => {
            const metierSlug = (a.metier || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const coverKey = Object.keys(DEMO_IMAGES).find(k => metierSlug.includes(k)) || 'default';
            
            // Générer un index DÉTERMINISTE basé sur l'ID de l'artisan
            const getStableIndex = (id) => {
              if (!id) return 0;
              const str = String(id);
              let hash = 0;
              for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash = hash & hash;
              }
              return Math.abs(hash);
            };
            
            let stableLogoIndex = getStableIndex(a.seller_id || a.id) % DEMO_LOGOS.length;
            
            // Si c'est la même photo que l'artisan précédent, décaler
            const previousIndex = usedIndices[index - 1];
            if (previousIndex !== undefined && stableLogoIndex === previousIndex) {
              stableLogoIndex = (stableLogoIndex + 1) % DEMO_LOGOS.length;
            }
            usedIndices.push(stableLogoIndex);
            
            return {
              id: a.seller_id || a.id,
              seller_id: a.seller_id,
              slug: a.seller_id || a.id,
              shop_name: a.company_name || a.artisan_name,
              metier: a.metier,
              description: a.description,
              city: a.city,
              postal_code: a.postal_code,
              phone: a.phone,
              is_verified: a.is_verified,
              is_demo: a.is_demo === true, // seuls les artisans marqués démo par l'API le sont
              rating_average: a.rating,
              reviews_count: a.reviews_count,
              rating: a.rating,
              cover_image: DEMO_IMAGES[coverKey],
              logo: DEMO_LOGOS[stableLogoIndex],
              services: a.prestations?.map(p => p.name) || [],
              certifications: a.certifications || [],
              zone_intervention: a.zone_intervention,
              zone_intervention_km: a.zone_intervention_km
            };
          });
          setApiArtisans(transformed);
        }
      } catch (error) {
        console.log('Using mock data only');
      }

      // Charger les boutiques publiées via le wizard (nouvelle architecture)
      try {
        const bParams = new URLSearchParams({ per_page: '50', sort_by: 'pertinence' });
        if (filters.ville) bParams.append('city', filters.ville);
        // Endpoint réel du backend en ligne : /api/boutique (au singulier)
        const bRes = await fetch(`${API_URL}/api/boutique?${bParams.toString()}`);
        if (bRes.ok) {
          const bData = await bRes.json();
          const transformed = (bData.boutiques || []).map(b => ({
            id: b.slug,
            seller_id: b.seller_id,
            slug: b.slug,
            shop_name: b.name,
            metier: b.metier_principal || b.metier || '',
            description: b.description,
            city: b.city,
            postal_code: b.postal_code,
            is_verified: b.is_verified,
            is_demo: false,
            rating_average: b.average_rating,
            rating: b.average_rating,
            reviews_count: b.reviews_count || 0,
            cover_image: b.cover_photos?.[0] || b.cover_photo || null,
            logo: b.logo_url || b.logo || null,
            services: [],
          }));
          setBoutiqueShops(transformed);
        }
      } catch (e) { /* ignoré */ }
    };
    loadArtisans();
  }, [filters.metiers, filters.metier, filters.ville, filters.rayon, filters.certifie, filters.prestation, filters.disponibilite, filters.note]);

  // Images de profil HOMMES uniquement - 20 photos artisans/professionnels (utilisées pour enrichir les mockés)
  const DEMO_LOGOS_MERGED = [
    'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1599566150163-29194dcabd36?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1639149888905-fb39731f2e6c?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1628157588553-5eeea00af15c?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1463453091185-61582044d556?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1583195764036-6dc248ac07d9?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=400&h=400&fit=crop',
  ];

  // Fonction de hash déterministe pour assigner des logos
  const getStableIndex = (id) => {
    if (!id) return 0;
    const str = String(id);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  // Fusionner les artisans mockés avec ceux de l'API - TOUS avec logos
  const allArtisans = useMemo(() => {
    // Dédupliquer: les boutiques wizard ne doivent pas apparaître deux fois
    const boutiqueSellerIds = new Set(boutiqueShops.map(b => b.seller_id).filter(Boolean));

    const usedIndices = [];

    const enrichedMocks = MOCK_ARTISANS.map((artisan, index) => {
      let stableIndex = getStableIndex(artisan.id) % DEMO_LOGOS_MERGED.length;
      const lastUsedIndex = usedIndices[usedIndices.length - 1];
      if (lastUsedIndex !== undefined && stableIndex === lastUsedIndex) {
        stableIndex = (stableIndex + 1) % DEMO_LOGOS_MERGED.length;
      }
      usedIndices.push(stableIndex);
      return {
        ...artisan,
        logo: artisan.logo || DEMO_LOGOS_MERGED[stableIndex]
      };
    });

    // Dédupliquer les artisans API (exclure ceux qui ont une boutique wizard)
    const filteredApiArtisans = apiArtisans.filter(a => !boutiqueSellerIds.has(a.seller_id));

    // Boutiques wizard en premier (données réelles), puis artisans API, puis mocks
    return [...boutiqueShops, ...filteredApiArtisans, ...enrichedMocks];
  }, [apiArtisans, boutiqueShops]);

  // Normalize: support both singular 'metier' (UnifiedFilterSystem) and plural 'metiers' (header/url)
  const effectiveMetiers = useMemo(() => {
    if (filters.metiers?.length > 0) return filters.metiers;
    if (filters.metier) return [filters.metier];
    return [];
  }, [filters.metiers, filters.metier]);

  // Calculer le nombre de résultats en fonction des filtres
  const filteredShops = useMemo(() => {
    let result = [...allArtisans];

    // Filtrer par métiers (supporte singular et plural)
    if (effectiveMetiers.length > 0) {
      result = result.filter(s =>
        effectiveMetiers.some(m =>
          s.metier?.toLowerCase().includes(m.toLowerCase())
        )
      );
    }

    // Filtrer par ville
    if (filters.ville) {
      result = result.filter(s =>
        s.city?.toLowerCase().includes(filters.ville.toLowerCase())
      );
    }

    // Filtrer par certifié
    if (filters.certifie) {
      result = result.filter(s => s.is_verified);
    }

    // Filtrer par note
    if (filters.note) {
      const minRating = parseFloat(filters.note);
      result = result.filter(s => (s.rating || s.rating_average || 0) >= minRating);
    }

    // Filtrer par rayon d'intervention (artisans sans zone_intervention_km ne sont pas filtrés)
    if (filters.rayon && filters.rayon !== 'all') {
      const minKm = parseInt(filters.rayon);
      result = result.filter(s =>
        !s.zone_intervention_km || s.zone_intervention_km >= minKm
      );
    }

    return result;
  }, [allArtisans, effectiveMetiers, filters.ville, filters.certifie, filters.note, filters.rayon]);

  useEffect(() => {
    setShops(filteredShops);
    setLoading(false);
  }, [filteredShops]);

  // Handle search from UnifiedHeader
  const handleHeaderSearch = ({ query, city, category }) => {
    const newFilters = { ...filters };
    if (category) {
      newFilters.metiers = [category];
    }
    if (city) {
      newFilters.ville = city;
    }
    setFilters(newFilters);
  };

  // Ouvrir le panneau de filtres sur une section spécifique
  const handleOpenFilter = (filterId) => {
    setActiveFilterSection(filterId);
    setShowFilters(true);
  };

  // Effacer un filtre spécifique
  const handleClearFilter = (type, value) => {
    const newFilters = { ...filters };
    
    if (type === 'metier' && value) {
      newFilters.metiers = filters.metiers?.filter(m => m !== value) || [];
      if (newFilters.metiers.length === 0) delete newFilters.metiers;
      if (filters.metier === value) delete newFilters.metier;
    } else if (type === 'ville') {
      delete newFilters.ville;
    } else if (type === 'rayon') {
      delete newFilters.rayon;
    } else if (type === 'note') {
      delete newFilters.note;
    } else if (type === 'disponibilite') {
      delete newFilters.disponibilite;
    } else if (type === 'certifie') {
      delete newFilters.certifie;
    }
    
    setFilters(newFilters);
  };

  // Effacer tous les filtres
  const clearAllFilters = () => {
    setFilters({});
  };

  // Appliquer les filtres
  const handleApplyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="artisans-page">
      {/* Header Unifié - Design System avec Recherche Intelligente */}
      {!embedded && <UnifiedHeader
        onSearch={handleHeaderSearch}
        onOpenFilters={() => setShowFilters(true)}
        artisansMode={true}
        artisansData={allArtisans}
      />}

      {/* Navigation Principale - Design System */}
      {!embedded && <MainNavigation onOpenFilters={() => setShowFilters(true)} />}

      {/* Spacer Mobile - 44px pour le menu horizontal fixed */}
      {!embedded && <div className="h-11 md:hidden" />}

      {/* Spacer Desktop/Tablet - compense la MainNavigation + sous-menu */}
      {!embedded && <div className="hidden md:block" style={{ height: '100px' }} />}

      {/* ═══ HERO BANNER ARTISANS — Mobile uniquement ═══ */}
      <div className="lg:hidden bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        {/* Version MOBILE - Bannière Premium Apple/Stripe - CLIQUABLE */}
        <div 
          className="md:hidden px-4 pt-6 pb-3 cursor-pointer active:opacity-90 transition-opacity"
          onClick={() => setShowFilters(true)}
          data-testid="artisans-banner-mobile"
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white whitespace-nowrap leading-none">
              Trouve ton artisan certifié
            </h1>
            <p className="text-white/90 text-sm font-normal whitespace-nowrap leading-none mt-2">
              En <span className="font-semibold">3 clics</span>, sans <span className="font-semibold">intermédiaire</span> ni <span className="font-semibold">commission</span>
            </p>
          </div>
        </div>
      </div>

      {/* ═══ TABS LISTE / CARTE — Mobile uniquement ═══ */}
      <div className="lg:hidden sticky top-[44px] z-30 bg-white border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setViewMode('liste')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
              viewMode === 'liste'
                ? 'text-[#F97316] border-b-2 border-[#F97316] bg-orange-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-liste"
          >
            <List size={18} />
            Liste
          </button>
          <button
            onClick={() => setViewMode('carte')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
              viewMode === 'carte'
                ? 'text-[#F97316] border-b-2 border-[#F97316] bg-orange-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-carte"
          >
            <Map size={18} />
            Carte
          </button>
        </div>
      </div>

      {/* ═══ CONTENU VUE CARTE — Mobile uniquement ═══ */}
      {viewMode === 'carte' && (
        <div className="lg:hidden h-[calc(100vh-180px)]">
          <ArtisansMapView embedded={true} />
        </div>
      )}

      {/* ═══ CONTENU VUE LISTE ═══ */}
      {viewMode === 'liste' && (
        <>
          {/* Badges de confiance - Masqué sur Desktop/Tablet */}

          {/* Filtres actifs - Ligne discrète */}
          <ActiveFilterBadges
            filters={{ ...filters, metiers: effectiveMetiers }}
            onClearFilter={handleClearFilter}
            onClearAll={clearAllFilters}
          />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-4">
        {/* Results Header simplifié */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
              {effectiveMetiers.length === 1
                ? `Artisans ${effectiveMetiers[0]}`
                : effectiveMetiers.length > 1
                  ? `Artisans (${effectiveMetiers.length} métiers)`
                  : 'Tous les artisans'}
              {filters.ville && ` à ${filters.ville}`}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              <span className="font-semibold text-gray-900">{shops.length}</span> professionnels trouvés
            </p>
          </div>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <CardSkeleton />
              </motion.div>
            ))}
          </div>
        ) : shops.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 p-12 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <Users size={40} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aucun artisan trouvé</h3>
            <p className="text-gray-500 mb-6">Essaie de modifier tes critères de recherche</p>
            <Button onClick={clearAllFilters} className="bg-[#1E40AF] hover:bg-[#1E3A8A] rounded-xl">
              Voir tous les artisans
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shops.map((shop, index) => (
              <ShopCard key={shop.id || shop.slug || index} shop={shop} index={index} />
            ))}
          </div>
        )}
      </main>
      
      {/* Bannière publicitaire Mobile - après la liste */}
      <AdBannerMobile className="my-6" />
      </>
      )}


      {/* ══════════════════════════════════════════════════════════════════════════
          SYSTÈME DE FILTRES UNIFIÉ - Amazon/Airbnb Style
      ══════════════════════════════════════════════════════════════════════════ */}
      <UnifiedFilterSystem
        context="artisans"
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={setFilters}
        resultsCount={filteredShops.length}
        onApply={handleApplyFilters}
      />
      
      {/* Footer Global */}
      
      
      {/* Bannières publicitaires Desktop */}
      <AdSidebarLeft />
      <AdSidebarRight />
      
      {/* Spacer mobile pour navigation */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
